import { useEffect, useRef } from 'react'
import { useMediaQuery } from '../../../../hooks/useMediaQuery'

/** SVG animation pauses off screen, in a background tab, and for reduced motion. */
export function useFieldAnimation(
  renderFrame: (element: SVGSVGElement, elapsed: number) => void,
  duration = Number.POSITIVE_INFINITY,
  stillFrame = 0,
) {
  const ref = useRef<SVGSVGElement>(null)
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  useEffect(() => {
    const element = ref.current
    if (!element) return
    renderFrame(element, reducedMotion ? stillFrame : 0)
    if (reducedMotion) return

    let frame = 0
    let elapsed = 0
    let previousTime = 0
    let lastPaint = 0
    let visible = true
    function tick(now: number) {
      frame = 0
      if (!visible || document.hidden) return
      elapsed = Math.min(duration, elapsed + (previousTime ? Math.min(now - previousTime, 80) : 0))
      previousTime = now
      if (now - lastPaint >= 1000 / 30 || elapsed >= duration) {
        renderFrame(element!, elapsed)
        lastPaint = now
      }
      if (elapsed < duration) frame = requestAnimationFrame(tick)
    }
    function resume() {
      cancelAnimationFrame(frame)
      frame = 0
      previousTime = 0
      if (visible && !document.hidden && elapsed < duration) frame = requestAnimationFrame(tick)
    }
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? true
      resume()
    }, { rootMargin: '80px' })
    observer.observe(element)
    document.addEventListener('visibilitychange', resume)
    resume()
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      document.removeEventListener('visibilitychange', resume)
    }
  }, [duration, reducedMotion, renderFrame, stillFrame])
  return ref
}
